<sui-toolbar #tb>
  @if (canSelectContent) {
    <sui-checkbox
      [checked]="selectAllChecked"
      (checkedChange)="onSelectAllChange($event)"
      [suiTooltip]="selectAllTooltip"
    ></sui-checkbox>
  }

  <!-- advanced quick-search self-dispatches in feed mode -->
  @if (canMassOps) {
    <app-advanced-quick-search
      [placeholder]="'feed-actions.placeholder.search' | translate"
      [value]="feedSearchQuery"
    ></app-advanced-quick-search>
  }

  <sui-button
    variant="ghost"
    size="sm"
    [iconOnly]="true"
    [leadingIcon]="['fas', 'refresh']"
    [ariaLabel]="'feed-actions.button.refresh-tooltip' | translate"
    [suiTooltip]="'feed-actions.button.refresh-tooltip' | translate"
    (click)="onRefresh()"
  ></sui-button>

  <!-- Selected mass-ops (only when a selection exists) -->
  @if (hasSelectedPosts) {
    <sui-button
      variant="ghost"
      [leadingIcon]="['fas', 'check-circle']"
      [trailingIcon]="['fas', 'caret-down']"
      [iconOnly]="tb.collapsed()"
      [ariaLabel]="tb.collapsed() ? selectedLabel : undefined"
      [suiTooltip]="tb.collapsed() ? selectedLabel : ''"
      [suiPopoverTrigger]="selectedPanel"
      suiPopoverPlacement="bottom-start"
      #selectedTrigger="suiPopoverTrigger"
      [expanded]="selectedTrigger.isOpen()"
    >
      {{
        tb.collapsed()
          ? selectedCount
          : selectedLabel + " (" + selectedCount + ")"
      }}
    </sui-button>
    <ng-template #selectedPanel>
      <div suiPopover class="dropdown-menu">
        @if (tb.collapsed()) {
          <div class="dropdown-heading">
            {{ selectedLabel }}
          </div>
        }
        @for (item of selectedItems; track item) {
          @if (item.isSeparator) {
            <div class="dropdown-divider"></div>
          }
          @if (item.isTitle) {
            <div class="dropdown-heading">
              {{ item.text }}
            </div>
          }
          @if (!item.isSeparator && !item.isTitle && item.isToggle) {
            <sui-checkbox
              class="dropdown-item"
              indicatorPosition="trailing"
              [checked]="showSelectedOnly"
              (checkedChange)="item.action?.()"
            >
              @if (item.icon) {
                <fa-icon [icon]="item.icon"></fa-icon>
              }
              <span>{{ item.text }}</span>
            </sui-checkbox>
          }
          @if (!item.isSeparator && !item.isTitle && !item.isToggle) {
            <a
              class="dropdown-item"
              (click)="
                item.action?.();
                $event.preventDefault();
                $event.stopPropagation()
              "
            >
              <span class="dropdown-item-link-col1">
                <span class="dropdown-item-link-label">
                  @if (item.icon) {
                    <div>
                      <fa-icon [icon]="item.icon"></fa-icon>
                    </div>
                  }
                  <span>{{ item.text }}</span>
                </span>
              </span>
            </a>
          }
        }
      </div>
    </ng-template>
  }

  <!-- Actions: feed-wide algorithms -->
  @if (canMassOps) {
    <sui-button
      class="right-group-start"
      variant="ghost"
      [leadingIcon]="['fas', 'bars']"
      [trailingIcon]="['fas', 'caret-down']"
      [iconOnly]="tb.collapsed()"
      [ariaLabel]="tb.collapsed() ? actionsLabel : undefined"
      [suiTooltip]="
        tb.collapsed()
          ? actionsLabel
          : ('feed-actions.button.actions-tooltip' | translate)
      "
      [suiPopoverTrigger]="actionsPanel"
      suiPopoverPlacement="bottom-end"
      #actionsTrigger="suiPopoverTrigger"
      [expanded]="actionsTrigger.isOpen()"
    >
      {{ tb.collapsed() ? "" : actionsLabel }}
    </sui-button>
    <ng-template #actionsPanel>
      <div suiPopover class="dropdown-menu">
        @if (tb.collapsed()) {
          <div class="dropdown-heading">
            {{ actionsLabel }}
          </div>
        }
        @for (item of actionItems; track item) {
          @if (item.isSeparator) {
            <div class="dropdown-divider"></div>
          }
          @if (item.isTitle) {
            <div class="dropdown-heading">
              {{ item.text }}
            </div>
          }
          @if (!item.isSeparator && !item.isTitle) {
            <a
              class="dropdown-item"
              (click)="
                item.action?.();
                $event.preventDefault();
                $event.stopPropagation()
              "
            >
              <span class="dropdown-item-link-col1">
                <span class="dropdown-item-link-label">
                  @if (item.icon) {
                    <div>
                      <fa-icon [icon]="item.icon"></fa-icon>
                    </div>
                  }
                  <span>{{ item.text }}</span>
                </span>
              </span>
              <span class="dropdown-item-link-col2">
                @if (item.secondaryAction) {
                  <button
                    type="button"
                    class="dropdown-item-secondary-action"
                    [title]="item.secondaryAction.tooltip || ''"
                    (click)="
                      item.secondaryAction.action();
                      $event.preventDefault();
                      $event.stopPropagation()
                    "
                  >
                    <fa-icon [icon]="item.secondaryAction.icon"></fa-icon>
                  </button>
                }
              </span>
            </a>
          }
        }
      </div>
    </ng-template>
  }

  <!-- View -->
  @if (canView) {
    <sui-button
      [class.right-group-start]="!canMassOps"
      variant="ghost"
      [leadingIcon]="['fas', 'eye']"
      [trailingIcon]="['fas', 'caret-down']"
      [iconOnly]="tb.collapsed()"
      [ariaLabel]="tb.collapsed() ? viewLabel : undefined"
      [suiTooltip]="
        tb.collapsed()
          ? viewLabel
          : ('feed-actions.button.view-tooltip' | translate)
      "
      [suiPopoverTrigger]="viewPanel"
      suiPopoverPlacement="bottom-end"
      #viewTrigger="suiPopoverTrigger"
      [expanded]="viewTrigger.isOpen()"
    >
      {{ tb.collapsed() ? "" : viewLabel }}
    </sui-button>
    <ng-template #viewPanel>
      <div suiPopover class="dropdown-menu view-dropdown-menu">
        @if (tb.collapsed()) {
          <div class="dropdown-heading">
            {{ viewLabel }}
          </div>
        }
        @for (group of viewGroups; track group) {
          @if (group.radioGroup) {
            <div
              sui-radio-group
              orientation="vertical"
              [ngModel]="group.value"
              [ngModelOptions]="{ standalone: true }"
              (valueChange)="onViewRadioChange(group, $event)"
            >
              @for (item of group.items; track item) {
                <sui-radio
                  class="dropdown-item"
                  indicatorPosition="trailing"
                  [value]="item.value"
                >
                  @if (item.icon) {
                    <fa-icon [icon]="item.icon"></fa-icon>
                  }
                  <span>{{ item.text }}</span>
                </sui-radio>
              }
            </div>
          } @else {
            @for (item of group.items; track item) {
              @if (item.isSeparator) {
                <div class="dropdown-divider"></div>
              }
              @if (item.isTitle) {
                <div class="dropdown-heading">
                  {{ item.text }}
                </div>
              }
              @if (!item.isSeparator && !item.isTitle) {
                <sui-checkbox
                  class="dropdown-item"
                  indicatorPosition="trailing"
                  [checked]="!!item.isSelected"
                  (checkedChange)="item.action?.()"
                >
                  @if (item.icon) {
                    <fa-icon [icon]="item.icon"></fa-icon>
                  }
                  <span>{{ item.text }}</span>
                </sui-checkbox>
              }
            }
          }
        }
      </div>
    </ng-template>
  }

  <!-- Sort -->
  @if (canSort) {
    <sui-button
      [class.right-group-start]="!canMassOps && !canView"
      variant="ghost"
      [leadingIcon]="sortDirIcon"
      [trailingIcon]="['fas', 'caret-down']"
      [iconOnly]="tb.collapsed()"
      [ariaLabel]="tb.collapsed() ? sortLabel : undefined"
      [suiTooltip]="
        tb.collapsed()
          ? sortLabel
          : ('feed-actions.button.sort-tooltip' | translate)
      "
      [suiPopoverTrigger]="sortPanel"
      suiPopoverPlacement="bottom-end"
      #sortTrigger="suiPopoverTrigger"
      [expanded]="sortTrigger.isOpen()"
    >
      {{ tb.collapsed() ? "" : sortLabel }}
    </sui-button>
    @if (sortFieldIcon) {
      <fa-icon class="sort-field-icon" [icon]="sortFieldIcon"></fa-icon>
    }
    <ng-template #sortPanel>
      <div suiPopover class="dropdown-menu">
        @if (tb.collapsed()) {
          <div class="dropdown-heading">
            {{ sortLabel }}
          </div>
        }
        <div
          sui-radio-group
          orientation="vertical"
          [ngModel]="sortField"
          [ngModelOptions]="{ standalone: true }"
          (valueChange)="onSortFieldChange($any($event))"
        >
          @for (item of sortFieldItems; track item) {
            <sui-radio
              class="dropdown-item"
              indicatorPosition="trailing"
              [value]="item.value"
            >
              @if (item.icon) {
                <fa-icon [icon]="item.icon"></fa-icon>
              }
              <span>{{ item.text }}</span>
            </sui-radio>
          }
        </div>
        <div class="dropdown-divider"></div>
        <div
          sui-radio-group
          orientation="vertical"
          [ngModel]="sortDirection"
          [ngModelOptions]="{ standalone: true }"
          (valueChange)="onSortOrderChange($any($event))"
        >
          @for (item of sortOrderItems; track item) {
            <sui-radio
              class="dropdown-item"
              indicatorPosition="trailing"
              [value]="item.value"
            >
              @if (item.icon) {
                <fa-icon [icon]="item.icon"></fa-icon>
              }
              <span>{{ item.text }}</span>
            </sui-radio>
          }
        </div>
      </div>
    </ng-template>
  }
</sui-toolbar>
